import type { ReactNode } from "react";
import { CalendarOutlined, UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { createSearchMatcher } from "../../../../utils/search";
import { TableCardClosingLabel } from "./TableCardClosingLabel";

// Маркира срещанията на `term` в `text` с топъл акцентен фон. Диапазоните идват
// от общия matcher (`utils/search.ts`) — същият, с който филтрираме — затова се
// оцветява и съвпадение на „шльокавица" („iv" в „Иван").
function Highlight({
  text,
  term,
  className,
}: {
  text: string;
  term: string;
  className?: string;
}) {
  // inline-block е критичен — иначе max-width/overflow/ellipsis не работят
  // и текстът прелива извън картата на тесни екрани (телефон/таблет).
  const cls = `inline-block ${className ?? ""}`;
  const matcher = createSearchMatcher(term);
  const ranges = matcher.isEmpty ? [] : matcher.ranges(text);
  if (ranges.length === 0) return <span className={cls}>{text}</span>;

  const parts: ReactNode[] = [];
  let cursor = 0;
  ranges.forEach((range, index) => {
    if (range.start > cursor) parts.push(text.slice(cursor, range.start));
    parts.push(
      <span
        key={index}
        className="bg-pos-disc-bg text-pos-disc-ink font-extrabold"
      >
        {text.slice(range.start, range.end)}
      </span>,
    );
    cursor = range.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <span className={cls}>{parts}</span>;
}

interface TableCardProps {
  /** Ref към бутона на картата — за стрелките и скрола до избраната маса. */
  cardRef: (element: HTMLButtonElement | null) => void;
  name: string;
  total: number;
  discountPercent: number;
  waiterLabel: string | null;
  clientLabel: string | null;
  /** Името на гостите на НАСТАНЕНАТА резервация, чиято е сметката (`reservation`
   *  от `GET /bills`); null — сметка без резервация. */
  reservationGuestName?: string | null;
  /** Броят гости горе вдясно (иконка с човече); null — не се показва.
   *  Родителят решава кога: само при „Въвеждане на брой гости" = при нова маса. */
  guests?: number | null;
  isSelected: boolean;
  isClosedEmpty: boolean;
  /** Закриването на масата още чака фискалния принтер (модалът „Закрий" вече
   *  е затворен): картата е сива, вместо сумата — „Закрива се" със спинер,
   *  и не се кликва. */
  isClosing?: boolean;
  /** „Карта — без бон": пари, взети от терминала, чийто бон не е излязъл. */
  cardPendingLabel?: string | null;
  searchTerm: string;
  compact: boolean;
  onClick: () => void;
  onDoubleClick?: () => void;
}

/** Картата на една маса в списъка на „Маркиране" (`TableList`). */
export function TableCard({
  cardRef,
  name,
  total,
  discountPercent,
  waiterLabel,
  clientLabel,
  reservationGuestName = null,
  guests = null,
  isSelected,
  isClosedEmpty,
  isClosing = false,
  cardPendingLabel = null,
  searchTerm,
  compact,
  onClick,
  onDoubleClick,
}: TableCardProps) {
  const { t } = useTranslation();
  // Избраната маса е с маркиран фон и акцентна рамка; закритата празна
  // (нефискализирана) — леко приглушен фон + сиви текстове, за да се
  // отличава от отворена празна сметка (напр. след пълно сторно). Масата в
  // процес на закриване е сива дори избрана — вместо сумата стои „Закрива се".
  const surface = isClosing
    ? "bg-pos-closed-bg border-pos-closed-brd"
    : isSelected
      ? "bg-pos-marked border-pos-accent"
      : isClosedEmpty
        ? "bg-pos-closed-bg border-pos-closed-brd"
        : "bg-pos-card border-pos-line";
  // Име с клиент носи акцентен цвят — визуален сигнал, че има закачен клиент.
  const nameColor = isClosedEmpty
    ? "text-pos-dim"
    : clientLabel
      ? "text-pos-accent-ink"
      : "text-pos-ink";

  return (
    <button
      ref={cardRef}
      type="button"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      disabled={isClosing}
      className={`min-w-0 text-left flex flex-col border-[1.5px] ${surface} ${
        isClosing ? "cursor-not-allowed" : "cursor-pointer"
      } ${
        compact
          ? "px-2 py-1.5 rounded-lg gap-1"
          : "rounded-[14px] p-[8px_14px] min-h-[64px] gap-1.5"
      }`}
    >
      {/* Горен ред: името на масата + (на чужда маса) сервитьорът, воден по
          ГОРНИЯ ръб на името (mt компенсира разликата в half-leading + cap
          height на двата кегела) — дългите текстове се режат с „…" и НЕ
          излизат от картата. leading-[1.2] (не none) — truncate клипва
          десцендерите при по-стегнат ред */}
      <div className="flex items-start gap-2 w-full min-w-0">
        <Highlight
          text={name}
          term={searchTerm}
          className={`flex-1 min-w-0 truncate font-bold leading-[1.2] ${nameColor} ${
            compact ? "text-sm" : "text-[21px]"
          }`}
        />
        {/* При закриване картата показва само името и „Закрива се" — без
            сервитьора името има цялата ширина („до телевизора") */}
        {waiterLabel && !isClosing && (
          <Highlight
            text={waiterLabel}
            term={searchTerm}
            className={`shrink-0 max-w-[55%] truncate text-pos-muted leading-[1.2] ${
              compact ? "mt-px text-[10px]" : "mt-[3px] text-[12px]"
            }`}
          />
        )}
        {/* Броят гости — най-вдясно, след името на сервитьора (ако го има) */}
        {guests != null && !isClosing && (
          <span
            title={t("ordering.guestCount.title")}
            className={`inline-flex shrink-0 items-center gap-1 font-semibold leading-[1.2] text-pos-muted ${
              compact ? "mt-px text-[10px]" : "mt-[3px] text-[12px]"
            }`}
          >
            <UserOutlined className="shrink-0" />
            {guests}
          </span>
        )}
      </div>

      {/* Долен ред: бадж отстъпка (пръв — подравнен с началото на името),
          настанената резервация и сума. Името на клиента НАРОЧНО не се
          показва — сигналът за закачен клиент е акцентният цвят на името на
          масата (nameColor). Резервацията е на същия ред — картата не расте:
          с бадж до нея е само иконката, без бадж — иконката и името на
          гостите, докъдето се събере („…"), без да мести сумата. */}
      {/* items-end + leading-none: долният ръб на баджа лежи точно на долния
          ръб на тотала */}
      <div className="mt-auto flex items-end gap-2 w-full min-w-0">
        {isClosing ? (
          <TableCardClosingLabel compact={compact} />
        ) : (
          <>
        {cardPendingLabel && (
          <span
            className={`shrink-0 font-semibold leading-none text-pos-disc-ink bg-pos-disc-bg rounded-md ${
              compact ? "text-[9px] px-1 py-px" : "text-[12px] px-[7px] py-0.5"
            }`}
          >
            {cardPendingLabel}
          </span>
        )}
        {discountPercent !== 0 && (
          <span
            className={`shrink-0 font-semibold leading-none text-pos-disc-ink bg-pos-disc-bg rounded-md ${
              compact ? "text-[9px] px-1 py-px" : "text-[12px] px-[7px] py-0.5"
            }`}
          >
            {/* >0 = отстъпка (−X%), <0 = надбавка (+X%) */}
            {discountPercent < 0
              ? `+${Math.abs(discountPercent)}%`
              : `-${discountPercent}%`}
          </span>
        )}
        {reservationGuestName && (
          <span
            title={t("ordering.seatedReservationTag", {
              name: reservationGuestName,
            })}
            // На широката карта вертикалните отстъпи са като на баджа отстъпка
            // (иконката е по средата му); на компактната — без, иначе редът
            // става по-висок от сумата и картата расте с 1px.
            className={`flex min-w-0 items-center font-semibold leading-none text-pos-ink2 ${
              compact ? "gap-1 text-[10px]" : "gap-1.5 py-0.5 text-[12px]"
            }`}
          >
            {/* `!` — `.anticon { color: inherit }` на antd е извън Tailwind layer-ите. */}
            <CalendarOutlined className="shrink-0 !text-pos-accent" />
            {!cardPendingLabel && discountPercent === 0 && (
              <Highlight
                text={reservationGuestName}
                term={searchTerm}
                className="min-w-0 truncate leading-[1.2]"
              />
            )}
          </span>
        )}
        <Highlight
          text={total.toFixed(2)}
          term={searchTerm}
          className={`ml-auto shrink-0 font-pos-mono font-semibold leading-none ${
            isClosedEmpty ? "text-pos-dim" : "text-pos-ink"
          } ${compact ? "text-[13px]" : "text-[19px]"}`}
        />
          </>
        )}
      </div>
    </button>
  );
}
